/**
 * Bookmarkable paths. Operator routes use Option B: scope prefix + asset id in path.
 */
import { MAKE_A_REQUEST_SCREEN_ID } from '@buxus/shared'
import {
  TRAINING_DETAIL_SCREEN_BY_LIST,
  TRAINING_LIST_PATHS,
  TRAINING_LIST_SCREEN_BY_DETAIL,
  TRAINING_LMS_SCREEN,
  TRAINING_SCREEN,
} from '../training/trainingScreens'
import {
  menuStackFromRoute,
  parseOperatorContentRoute,
  pathForOperatorRoute,
  routeFromMenuStack,
  type OperatorContentRoute,
  type OperatorUrlSlugResolver,
} from './operatorRoute'

export type { OperatorContentRoute, OperatorUrlSlugResolver, RouteScope } from './operatorRoute'
export {
  DEFAULT_OPERATOR_SCREEN,
  menuStackFromRoute,
  pathForMenuStackPrefix,
  pathForOperatorRoute,
  routeFromMenuStack,
} from './operatorRoute'

const SHELL_SINGLE: Record<string, string> = {
  __download__: '/download',
  __operators__: '/operators',
  __home__: '/home',
  [TRAINING_SCREEN]: '/training',
  [TRAINING_LMS_SCREEN]: '/training/lms',
  ...TRAINING_LIST_PATHS,
  __alert__: '/send-alert',
  __profile__: '/profile',
  __information__: '/information',
}

const PATH_TO_SHELL: Record<string, string> = Object.fromEntries(
  Object.entries(SHELL_SINGLE).map(([k, v]) => [v, k]),
)

export function pathForShellScreen(screenId: string): string | null {
  const listId = TRAINING_LIST_SCREEN_BY_DETAIL[screenId]
  if (listId) return SHELL_SINGLE[listId] ?? null
  return SHELL_SINGLE[screenId] ?? null
}

export function shellScreenFromPath(pathname: string): string | null {
  const n = pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname
  return PATH_TO_SHELL[n] ?? null
}

export type PathForAppStateOptions = {
  assetId?: string | null
  /** Filename / catalog id for a training document detail URL. */
  trainingDocId?: string | null
}

function pathForTrainingDetail(screenId: string, trainingDocId: string | null | undefined): string | null {
  const listId = TRAINING_LIST_SCREEN_BY_DETAIL[screenId]
  if (!listId) return null
  const base = SHELL_SINGLE[listId]
  if (!base) return null
  const id = trainingDocId?.trim()
  if (!id) return base
  return `${base}/${encodeURIComponent(id)}`
}

function parseTrainingDetailPath(pathname: string): { screenId: string; trainingDocId: string } | null {
  for (const [listId, detailId] of Object.entries(TRAINING_DETAIL_SCREEN_BY_LIST)) {
    const base = SHELL_SINGLE[listId]
    if (!base) continue
    const prefix = `${base}/`
    if (!pathname.startsWith(prefix)) continue
    const raw = pathname.slice(prefix.length)
    if (!raw) continue
    try {
      const trainingDocId = decodeURIComponent(raw).trim()
      if (!trainingDocId) continue
      return { screenId: detailId, trainingDocId }
    } catch {
      return null
    }
  }
  return null
}

/** Single top-level routes for shell screens; operator content uses Option B paths. */
export function pathForAppState(
  operatorId: string | null,
  stack: string[],
  urlSlugForOperator?: OperatorUrlSlugResolver,
  options?: PathForAppStateOptions,
): string {
  if (stack.length === 0) return '/operators'
  if (stack.length === 1) {
    const screenId = stack[0]!
    const detailPath = pathForTrainingDetail(screenId, options?.trainingDocId)
    if (detailPath) return detailPath
    const p = pathForShellScreen(screenId)
    if (p) return p
  }
  if (!operatorId) return '/operators'
  const route = routeFromMenuStack(stack, options?.assetId)
  return pathForOperatorRoute(operatorId, route, urlSlugForOperator)
}

export type ParsedAppPath =
  | { kind: 'shell'; screenId: string; trainingDocId?: string }
  | { kind: 'operatorsIndex' }
  | { kind: 'operator'; operatorSegment: string; stack: string[]; route: OperatorContentRoute }

export function parseAppPath(pathname: string): ParsedAppPath | null {
  const raw = pathname.trim() || '/'
  const n = raw.replace(/\/+$/, '') || '/'

  const shell = shellScreenFromPath(n)
  if (shell) return { kind: 'shell', screenId: shell }

  const trainingDetail = parseTrainingDetailPath(n)
  if (trainingDetail) {
    return { kind: 'shell', screenId: trainingDetail.screenId, trainingDocId: trainingDetail.trainingDocId }
  }

  if (n === '/' || n === '/operators') {
    return { kind: 'operatorsIndex' }
  }

  const parts = n.split('/').filter(Boolean)
  if (parts[0] !== 'operators' || !parts[1]) {
    return null
  }

  const operatorSegment = decodeURIComponent(parts[1])
  const rest = parts.slice(2)
  const route = parseOperatorContentRoute(rest)
  if (!route) return null

  let stack = menuStackFromRoute(route)
  if (route.screenId === MAKE_A_REQUEST_SCREEN_ID) {
    stack = stack.filter((sid, i, arr) => sid !== MAKE_A_REQUEST_SCREEN_ID || i === arr.length - 1)
  }

  return { kind: 'operator', operatorSegment, stack, route }
}

/** Top-level shell stack id for a pathname, or null for operator content routes. */
export function shellScreenIdFromPathname(pathname: string): string | null {
  const parsed = parseAppPath(pathname)
  if (parsed?.kind === 'shell') return parsed.screenId
  if (parsed?.kind === 'operatorsIndex') return '__operators__'
  return null
}

/** Stable route key for analytics dedupe (`/` ≡ `/operators` for the operators shell). */
export function analyticsRoutePathKey(pathname: string): string {
  const parsed = parseAppPath(pathname)
  if (parsed?.kind === 'shell') {
    if (parsed.trainingDocId) {
      return pathname.replace(/\/+$/, '') || '/'
    }
    return pathForShellScreen(parsed.screenId) ?? pathname
  }
  const shellId = shellScreenIdFromPathname(pathname)
  if (shellId) return pathForShellScreen(shellId) ?? pathname
  const n = pathname.replace(/\/+$/, '') || '/'
  return n
}
